<template>
  <div
    class="flex items-end space-x-3 mb-3"
    :class="[
      message.isSenderLocation || message.isSenderSystem || message.isSenderEmployee ? 'justify-end' : 'justify-start'
    ]"
  >
    <div v-if="message.isSenderCustomer" class="flex-shrink-0 relative transition-all duration-300 ease-in-out" :class="[ isActiveMessage ? 'mb-9' : 'mb-2' ]">
      <OAvatar :items="[message.customer]" />
    </div>

    <div
      :class="[
        'flex flex-col max-w-[80%]',
        message.isSenderLocation || message.isSenderSystem ? 'items-end' : 'items-start'
      ]"
    >
      <div
        v-if="message.isSenderSystem || !message.isTypeGeneral"
        class="flex items-center mb-1"
        :class="[
          message.isSenderLocation || message.isSenderSystem || message.isSenderEmployee ? 'flex-row-reverse' : 'flex-row'
        ]"
      >
        <OIcon icon="sparkles" :size="16" class="opacity-60" />
        <p class="text-xs opacity-60 px-1">
          Automated message
        </p>
      </div>

      <template v-if="message.attachments">
        <AttachmentsEmbedList
          :attachments="message.attachments"
          :class="[ message.body ? 'mb-3' : 'mb-1' ]"
        />
      </template>

      <div
        v-if="message.body"
        class="px-3 py-2 rounded-lg shadow-md cursor-pointer transition-all duration-300 ease-in-out"
        :class="[
          {
            'bg-primary-600 dark:bg-primary-500/50 text-white ': message.isSenderLocation || message.isSenderEmployee || message.isSenderSystem,
            'bg-gray-100 text-gray-800  dark:bg-gray-800 dark:text-white': message.isSenderCustomer,
          },
          isActiveMessage ? 'mb-1' : 'mb-2'
        ]"
        @click="_active = message.idCustomerMessage"
      >
        <div>
          {{ message.body || $t('components.message.elements.message.noContent') }}
        </div>
      </div>

      <div
        :class="[
          'text-sm flex items-start space-x-3 overflow-hidden transition-all duration-300 ease-in-out',
          isActiveMessage ? 'h-8' : 'h-[0]'
        ]"
      >
        <template v-if="!message.isSenderCustomer">
          <div
            v-if="!message.isChannelEmailDoNotSend"
            :class="[
              'flex items-center',
              message.isChannelEmailFailed ? 'text-red-600' : 'text-gray-500'
            ]"
          >
            <p class="mr-1">{{ $t('general.email') }}</p>

            <OIcon v-if="message.isChannelEmailFailed" icon="x" :size="8" fill />
            <OIcon v-if="message.isChannelEmailSent || message.isChannelEmailOpened" icon="check" :size="8" fill />
            <OIcon v-if="message.isChannelEmailOpened" icon="check" :size="8" fill class="-ml-1" />
          </div>
          <div
            v-if="!message.isChannelSMSDoNotSend"
            :class="[
              'flex items-center',
              message.isChannelSMSFailed ? 'text-red-600' : 'text-gray-500'
            ]"
          >
            <p class="mr-1">{{ $t('general.sms') }}</p>

            <OIcon v-if="message.isChannelSMSFailed" icon="x" :size="8" fill />
            <OIcon v-if="message.isChannelSMSSent || message.isChannelSMSOpened" icon="check" :size="8" fill />
            <OIcon v-if="message.isChannelSMSOpened" icon="check" :size="8" fill class="-ml-1" />
          </div>
          <div
            v-if="!message.isChannelAppDoNotSend"
            :class="[
              'flex items-center',
              message.isChannelAppFailed ? 'text-red-600' : 'text-gray-500'
            ]"
          >
            <p class="mr-1">{{ $t('general.bookapp') }}</p>

            <OIcon v-if="message.isChannelAppFailed" icon="x" :size="8" fill />
            <OIcon v-if="message.isChannelAppSent || message.isChannelAppOpened" icon="check" :size="8" fill />
            <OIcon v-if="message.isChannelAppOpened" icon="check" :size="8" fill class="-ml-1" />
          </div>
        </template>

        <template v-if="message.isSenderCustomer">
          <div
            v-if="message.isChannelEmailOrigin"
            :class="[
              'flex items-center space-x-1 text-gray-500'
            ]"
          >
            <p>{{ $t('components.message.elements.message.sentViaEmail') }}</p>
          </div>
          <div
            v-else-if="message.isChannelSMSOrigin"
            :class="[
              'flex items-center space-x-1 text-gray-500'
            ]"
          >
            <p>{{ $t('components.message.elements.message.sentViaSMS') }}</p>
          </div>
          <div
            v-else-if="message.isChannelAppOrigin"
            :class="[
              'flex items-center space-x-1 text-gray-500'
            ]"
          >
            <p>{{ $t('components.message.elements.message.sentViaApp') }}</p>
          </div>
        </template>
      </div>
    </div>

    <div v-if="message.isSenderEmployee" class="flex-shrink-0 relative transition-all duration-300 ease-in-out" :class="[ isActiveMessage ? 'mb-9' : 'mb-2' ]">
      <OAvatar :items="[message.employee]" />
    </div>

    <div v-if="message.isSenderLocation" class="flex-shrink-0 relative transition-all duration-300 ease-in-out" :class="[ isActiveMessage ? 'mb-9' : 'mb-2' ]">
      <OAvatar :items="[$auth.info.location]" />
    </div>

    <div v-if="message.isSenderSystem" class="flex-shrink-0 relative" :class="[ isActiveMessage ? 'mb-9' : 'mb-2' ]">
      <OAvatar :items="[$auth.info.location]" />
    </div>
  </div>
</template>

<script setup lang="ts">
import AttachmentsEmbedList from '@/components/message/elements/attachments/embed/List.vue'

import {
  OAvatar,
} from '@/components/elements'

interface Props {
  message: Record<string, any>
  lastRead?: Record<string, any> | null
}

const props = withDefaults(defineProps<Props>(), {
  lastRead: null,
})

const { $date } = useNuxtApp()

const _active = defineModel<number | null>('active')

const createdDateLabel = computed(() => {
  return $date.format(props.message.created * 1000, 'HH:MM dd MMM yyyy')
})

const isLastReadMessage = computed(() => {
  return props.message.idCustomerMessage === props.lastRead?.idCustomerMessage
})

const isActiveMessage = computed(() => {
  return _active.value === props.message.idCustomerMessage
})
</script>
